'use client';

import { sdk } from '@/sdk';
import useInsights from '@/hooks/useInsights';
import CartProcessingModal from '@/components/organisms/cart-processing-modal';
import { SWRProvider } from './swr';
import { AppProvidersProps } from './types';
import ShipAndLanguageProvider from './ship-and-language';
import StoreAndBusinessUnitsProvider from './store-and-business-units';
import AlgoliaKeyProvider from './algolia-key';
import MutationLockProvider from './mutation-lock';

// Side-effect-only component: drives the Algolia Insights init + the
// anon→authenticated stitching once per session. Mounted inside the
// SWRProvider so `useAccount` resolves; renders nothing. PR-B wires
// the actual event firing.
const InsightsBootstrap = () => {
  useInsights();
  return null;
};

// EWM-482 — the persistent app-wide data/state tree. Mounted ONCE in the
// [locale] layout so the SWR cache + context survive client navigation (no
// remount, no per-page refetch). The route-specific `TracingProvider` stays
// page-level — it needs the page's PageResponse, which the layout doesn't have.
export const AppProviders = ({
  locale,
  initialData,
  algoliaSecuredKey,
  children,
}: React.PropsWithChildren<AppProvidersProps>) => {
  sdk.defaultConfigure(locale);

  return (
    <SWRProvider
      value={{
        fallback: {
          '/action/account/getAccount': { data: initialData.account },
          '/action/business-unit/getBusinessUnits': { data: initialData.businessUnits },
          '/action/business-unit/getAssociate': { data: initialData.associate },
          '/action/project/getProjectSettings': { data: initialData.projectSettings },
          '/action/product/queryCategories?format=flat': { data: initialData.flatCategories },
          '/action/product/queryCategories?format=tree': { data: initialData.treeCategories },
        },
      }}
    >
      <ShipAndLanguageProvider projectSettings={initialData.projectSettings}>
        <StoreAndBusinessUnitsProvider activeSelection={initialData.activeSelection}>
          <AlgoliaKeyProvider securedApiKey={algoliaSecuredKey?.key} validUntil={algoliaSecuredKey?.validUntil}>
            {/* EWM-521 / EWM-612 — scoped mutation tracker; wraps everything so a cart
                write surfaces the processing modal below (and account/BU writes stay in
                their own scope) without the old site-wide freeze. */}
            <MutationLockProvider>
              <InsightsBootstrap />
              <CartProcessingModal />
              {children}
            </MutationLockProvider>
          </AlgoliaKeyProvider>
        </StoreAndBusinessUnitsProvider>
      </ShipAndLanguageProvider>
    </SWRProvider>
  );
};
